// Catalog.ID Client — The Authenticator Key (TOTP)
// ============================================================================
// registration-restructure §15.3/§15.6. The entropy ceremony mints a 20-byte
// secret, the kit prints it on its own sheet as a QR (an `otpauth://` URI) and
// as 32 base32 characters in groups of four, and the node turns it into the
// six-digit codes an authenticator app produces.
//
// RFC 6238 over HMAC-SHA1, six digits, thirty-second steps — the parameters
// every app on the market supports and several silently ignore any attempt to
// change. Do NOT "upgrade" them.
//
// The golden vectors here are RFC 6238 Appendix B's SHA-1 set, pinned
// identically in the server's `TotpServiceTests`, so the sheet, the browser and
// the node meet on one format.
//
// `totpCode` is NOT used by production UI — nothing in the browser ever needs to
// compute a code; the member's phone does that. It exists for the tests and for
// the Playwright login helper, which logs in past the gate.

import { hmac } from '@noble/hashes/hmac.js';
import { sha1 } from '@noble/hashes/legacy.js';

/** 20 bytes — RFC 4226's recommendation, and 32 base32 characters on paper. */
export const TOTP_SECRET_LENGTH = 20;

export const TOTP_DIGITS = 6;
export const TOTP_PERIOD_SECONDS = 30;

/** The issuer/label prefix baked into the otpauth URI. */
export const TOTP_ISSUER = 'Catalog.ID';

const BASE32_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567';

/** RFC 4648 base32, uppercase, NO padding. */
export function base32Encode(data: Uint8Array): string {
    let out = '';
    let buffer = 0;
    let bits = 0;
    for (const byte of data) {
        buffer = (buffer << 8) | byte;
        bits += 8;
        while (bits >= 5) {
            out += BASE32_ALPHABET[(buffer >> (bits - 5)) & 31];
            bits -= 5;
        }
    }
    if (bits > 0) out += BASE32_ALPHABET[(buffer << (5 - bits)) & 31];
    return out;
}

/** Decodes uppercase or lowercase, with or without padding, and ignores the
 *  spaces and dashes the printed sheet groups the characters with. Throws on
 *  anything else — a typed secret that is not base32 is a typo, not a policy
 *  question. */
export function base32Decode(value: string): Uint8Array {
    const bytes: number[] = [];
    let buffer = 0;
    let bits = 0;
    for (const raw of value) {
        if (raw === ' ' || raw === '-' || raw === '=' || raw === '\t' || raw === '\n' || raw === '\r') continue;
        const index = BASE32_ALPHABET.indexOf(raw.toUpperCase());
        if (index < 0) throw new Error(`"${raw}" is not a base32 character`);
        buffer = (buffer << 5) | index;
        bits += 5;
        if (bits >= 8) {
            bytes.push((buffer >> (bits - 8)) & 0xff);
            bits -= 8;
        }
    }
    return new Uint8Array(bytes);
}

/** 20 CSPRNG bytes — Settings' *Issue a new key* mints one here; registration
 *  takes its secret from the ceremony instead (§15.6). */
export function generateTotpSecret(): Uint8Array {
    const secret = new Uint8Array(TOTP_SECRET_LENGTH);
    crypto.getRandomValues(secret);
    return secret;
}

/**
 * The QR payload — printed on the Authenticator Key sheet and shown on screen
 * wherever the key is enrolled. The label is `Catalog.ID:<cid>`: the account
 * id, never a name or a channel. The issuer parameter repeats the prefix, which
 * is the form every app parses.
 */
export function otpauthUri(cid: string, secret: Uint8Array): string {
    return `otpauth://totp/${encodeURIComponent(`${TOTP_ISSUER}:${cid}`)}`
        + `?secret=${base32Encode(secret)}`
        + `&issuer=${encodeURIComponent(TOTP_ISSUER)}`
        + `&algorithm=SHA1&digits=${TOTP_DIGITS}&period=${TOTP_PERIOD_SECONDS}`;
}

/** The 32 characters as they are printed and typed: eight groups of four. */
export function formatAuthenticatorKey(secret: Uint8Array): string {
    return (base32Encode(secret).match(/.{1,4}/g) ?? []).join(' ');
}

/**
 * RFC 6238: counter = ⌊unix seconds / 30⌋ as eight big-endian bytes, HMAC-SHA1,
 * dynamic truncation (offset = low nibble of the last byte, a 31-bit integer
 * from the four bytes there), mod 10^6, zero-padded.
 */
export function totpCode(secret: Uint8Array, unixSeconds: number): string {
    const counter = Math.floor(unixSeconds / TOTP_PERIOD_SECONDS);
    const counterBytes = new Uint8Array(8);
    new DataView(counterBytes.buffer).setBigUint64(0, BigInt(counter), false);

    const digest = hmac(sha1, secret, counterBytes);
    const offset = digest[digest.length - 1] & 0x0f;
    const binary =
        ((digest[offset] & 0x7f) << 24) |
        ((digest[offset + 1] & 0xff) << 16) |
        ((digest[offset + 2] & 0xff) << 8) |
        (digest[offset + 3] & 0xff);

    return String(binary % 10 ** TOTP_DIGITS).padStart(TOTP_DIGITS, '0');
}
